import React, { useState, useMemo } from 'react';
import { useTemplateStore, TEMPLATES, type TemplateType, type TemplateCategory } from '../../stores/templateStore';
import { generateTemplateHTML } from '../../stores/templateStore';

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (templateId: TemplateType) => void;
}

const categories: { id: TemplateCategory; name: string }[] = [
  { id: 'all', name: '全部' },
  { id: 'education', name: '教育' },
  { id: 'product', name: '产品' },
  { id: 'event', name: '活动' },
  { id: 'company', name: '企业' },
  { id: 'resource', name: '资源' },
];

// 模板图标映射
const templateIcons: Record<string, React.ReactNode> = {
  'graduation-cap': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M12 14l9-5-9-5-9 5 9 5z" />
      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  'box-open': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  ),
  'calendar-alt': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  'building': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  'download': (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
};

// 分类颜色映射
const categoryColors: Record<TemplateCategory, { bg: string; text: string; border: string }> = {
  education: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  product: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  event: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  company: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  resource: { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-200' },
  all: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { currentTemplateId, setCurrentTemplate } = useTemplateStore();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>(currentTemplateId);
  const [activeCategory, setActiveCategory] = useState<TemplateCategory>('all');
  const [isApplying, setIsApplying] = useState(false);

  // 过滤模板
  const filteredTemplates = useMemo(() => {
    if (activeCategory === 'all') return Object.values(TEMPLATES);
    return Object.values(TEMPLATES).filter((t) => t.category === activeCategory);
  }, [activeCategory]);

  // 获取预览HTML
  const previewHtml = useMemo(() => {
    return generateTemplateHTML(selectedTemplate);
  }, [selectedTemplate]);

  // 处理模板选择
  const handleTemplateClick = (templateId: TemplateType) => {
    setSelectedTemplate(templateId);
  };

  // 确认选择
  const handleConfirm = async () => {
    setIsApplying(true);
    
    // 模拟应用延迟
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    setCurrentTemplate(selectedTemplate);
    onSelect?.(selectedTemplate);
    onClose();
    setIsApplying(false);
  };

  // 获取当前选中的模板信息
  const currentTemplate = TEMPLATES[selectedTemplate];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[80vh] max-h-[700px] flex flex-col shadow-2xl">
        {/* 头部 */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">选择模板</h2>
            <p className="text-sm text-gray-500 mt-1">选择一个模板开始你的设计</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 主体 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧：模板列表 */}
          <div className="w-80 border-r flex flex-col">
            {/* 分类筛选 */}
            <div className="p-4 border-b">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                      activeCategory === cat.id
                        ? 'bg-purple-100 text-purple-700 font-medium'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* 模板列表 */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-3">
                {filteredTemplates.map((template) => {
                  const colors = categoryColors[template.category];
                  const isSelected = selectedTemplate === template.id;

                  return (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateClick(template.id as TemplateType)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-transparent bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.bg} ${colors.text}`}
                        >
                          {templateIcons[template.icon]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900">
                            {template.name}
                          </div>
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {template.description}
                          </div>
                          <div className="mt-2">
                            <span
                              className={`inline-block px-2 py-0.5 text-xs rounded-full ${colors.bg} ${colors.text}`}
                            >
                              {categories.find((c) => c.id === template.category)?.name}
                            </span>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="text-purple-500">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 右侧：预览 */}
          <div className="flex-1 flex flex-col bg-gray-50">
            <div className="flex-1 p-6 overflow-hidden">
              <div className="bg-white rounded-xl shadow-lg h-full overflow-hidden">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  title={`${currentTemplate.name} Preview`}
                  sandbox="allow-scripts"
                />
              </div>
            </div>

            {/* 底部信息 */}
            <div className="px-6 py-4 bg-white border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900">
                    已选择: {currentTemplate.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {currentTemplate.description}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isApplying}
                    className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isApplying ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        应用中...
                      </>
                    ) : (
                      <>
                        确认选择
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};